import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    contentChildren,
    DestroyRef,
    effect,
    ElementRef,
    inject,
    input,
    InputSignal,
    output,
    Signal,
    signal,
    viewChild,
    viewChildren,
    WritableSignal,
} from '@angular/core';

import { CreateEntityButtonComponent } from '@connect-services/client/components/create-entity-button/create-entity-button.component';
import { ProjectEntityTableService } from '@connect-services/client/services/project-entity-table.service';
import { ButtonComponent, ButtonType } from '@connect-services/src/lib/components/button/button.component';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { TabContentDirective } from '@shared/client/directives/tab-content.directive';

export type TabWithState = {
    active: boolean;
    canCreate?: boolean;
    createDisabled?: boolean;
    createTooltipKey?: string;
    createVisible?: boolean;
    disabled?: boolean;
    disabledTooltipKey?: string;
    name: string;
};

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ButtonComponent, CreateEntityButtonComponent, RessourcesModule],
    selector: 'app-tab',
    styleUrl: './tab.component.scss',
    templateUrl: './tab.component.html',
})
export class TabComponent implements AfterViewInit {
    public readonly activeTabColor = input<string>('bg-on-primary');
    public readonly canAddTab = input<boolean>(false);
    public changeTab = output<number>();
    public create = output<number>();
    public createTab = output<void>();
    public readonly currentTabIndex = signal<number>(0);
    public readonly defaultTabIndex = input<number>(0);
    public readonly isSimpleTabStyle = input<boolean>(false);
    public readonly isTruncateAllowed = input<boolean>(true);
    public readonly showScrollLeft: WritableSignal<boolean> = signal(false);
    public readonly showScrollRight: WritableSignal<boolean> = signal(false);
    public readonly tabClasses: Signal<string[]> = computed(() => {
        const highlightedTabName = this.highlightedTabName();

        return this.tabs().map((tab) => {
            const isSelected = highlightedTabName === tab.name;
            const isDisabled = tab.disabled === true;

            const inactiveClass = tab.active ? '' : '!text-custom-dark-grey';
            const selectedClass = isSelected ? `${this.activeTabColor()} !opacity-100` : '';
            const disabledClass = isDisabled && !isSelected ? 'cursor-not-allowed' : 'cursor-pointer';

            return `${inactiveClass} ${selectedClass} ${disabledClass}`.trim();
        });
    });
    public readonly tabContents: Signal<readonly TabContentDirective[]> = contentChildren(TabContentDirective);
    public readonly tabs: InputSignal<TabWithState[]> = input.required<TabWithState[]>();
    public readonly tabsBadge: InputSignal<boolean[] | undefined> = input<boolean[] | undefined>(undefined);
    public readonly translateTabs: InputSignal<boolean> = input<boolean>(true);

    protected readonly highlightedTabName: Signal<string | null> = computed(() => {
        return this.tabs()[this.currentTabIndex()]?.name ?? null;
    });
    // eslint-disable-next-line @typescript-eslint/member-ordering
    protected readonly ButtonType = ButtonType;

    private readonly arrowsButtonsPadding: number = 60;
    private readonly destroyRef = inject(DestroyRef);
    private intersectionObserver?: IntersectionObserver;
    private readonly projectEntityTableService = inject(ProjectEntityTableService);
    private resizeObserver?: ResizeObserver;
    private readonly scrollAmount: number = 150;
    private readonly tabElements: Signal<readonly ElementRef[]> = viewChildren<ElementRef>('tabElement');
    private readonly tabsContainer: Signal<ElementRef> = viewChild.required<ElementRef>('tabsContainer');

    public constructor() {
        this.destroyRef.onDestroy(() => {
            this.intersectionObserver?.disconnect();
            this.resizeObserver?.disconnect();
        });

        effect(() => {
            const tabs = this.tabs();
            const defaultIndex = this.getValidIndex(tabs, this.defaultTabIndex());
            const firstEnabledIndex = this.getFirstEnabledIndex(tabs, defaultIndex);
            this.currentTabIndex.set(firstEnabledIndex);
        });
    }

    public checkScrollButtons(): void {
        const container = this.tabsContainer().nativeElement;
        const max = container.scrollWidth - container.clientWidth;
        this.showScrollLeft.set(container.scrollLeft > 0);
        this.showScrollRight.set(container.scrollLeft < max - 1);
    }

    public isTruncated(index: number): boolean {
        if (!this.tabElements()?.length) {
            return false;
        }

        const tabElement = this.tabElements()[index]?.nativeElement as HTMLElement;
        if (!tabElement) {
            return false;
        }

        return tabElement.offsetWidth < tabElement.scrollWidth;
    }

    public ngAfterViewInit(): void {
        const container = this.tabsContainer().nativeElement;

        this.resizeObserver = new ResizeObserver(() => this.checkScrollButtons());
        this.resizeObserver.observe(container);

        this.intersectionObserver = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry: IntersectionObserverEntry) => entry.isIntersecting)) {
                    this.checkScrollButtons();
                }
            },
            { root: null, threshold: 0.01 },
        );

        this.intersectionObserver.observe(container);
    }

    public onCreateClick(index: number): void {
        const validIndex: number = this.getValidIndex(this.tabs(), index);
        const selectedTab: TabWithState = this.tabs()[validIndex];

        if (!selectedTab || selectedTab.disabled || selectedTab.createDisabled) {
            return;
        }

        this.currentTabIndex.set(validIndex);
        this.scrollToTab(validIndex);

        this.projectEntityTableService.closeEntityDetailPanel();
        this.create.emit(validIndex);
    }

    public onTabClick(index: number): void {
        const validIndex: number = this.getValidIndex(this.tabs(), index);
        const selectedTab: TabWithState = this.tabs()[validIndex];

        if (!selectedTab || selectedTab.disabled) {
            return;
        }

        const shouldEmitChange = this.currentTabIndex() !== validIndex;

        this.currentTabIndex.set(validIndex);
        this.scrollToTab(validIndex);

        if (shouldEmitChange) {
            this.changeTab.emit(validIndex);
        }
    }

    public scrollLeft(): void {
        this.scrollTabs(-this.scrollAmount);
    }

    public scrollRight(): void {
        this.scrollTabs(this.scrollAmount);
    }

    private getFirstEnabledIndex(tabList: TabWithState[], preferredIndex: number): number {
        const preferredTab = tabList[preferredIndex];

        if (preferredTab && preferredTab.disabled !== true) {
            return preferredIndex;
        }

        const firstEnabledIndex = tabList.findIndex((tab) => tab.disabled !== true);
        if (firstEnabledIndex >= 0) {
            return firstEnabledIndex;
        }

        return 0;
    }

    private getValidIndex(tabs: TabWithState[], index: number): number {
        if (index >= 0 && index < tabs.length) {
            return index;
        }

        return 0;
    }

    private scrollTabs(amount: number): void {
        const container = this.tabsContainer().nativeElement;
        container.scrollBy({ behavior: 'smooth', left: amount });
        this.checkScrollButtons();
    }

    private scrollToTab(index: number): void {
        const container = this.tabsContainer().nativeElement;
        const tabElements = container.children;
        const selectedTab = tabElements[index];
        const tabOffsetLeft = selectedTab.offsetLeft;
        const tabWidth = selectedTab.offsetWidth;
        const containerWidth = container.clientWidth;

        if (tabOffsetLeft < container.scrollLeft) {
            container.scrollTo({ behavior: 'smooth', left: tabOffsetLeft });

            return;
        }

        if (tabOffsetLeft + tabWidth > container.scrollLeft + containerWidth) {
            container.scrollTo({
                behavior: 'smooth',
                left: tabOffsetLeft + tabWidth - containerWidth + this.arrowsButtonsPadding,
            });

            return;
        }

        if (tabOffsetLeft < container.scrollLeft + this.arrowsButtonsPadding) {
            container.scrollTo({ behavior: 'smooth', left: tabOffsetLeft });

            return;
        }

        if (tabOffsetLeft + tabWidth > container.scrollLeft + containerWidth - this.arrowsButtonsPadding) {
            container.scrollTo({
                behavior: 'smooth',
                left: tabOffsetLeft + tabWidth - containerWidth + this.arrowsButtonsPadding,
            });
        }
    }
}
